import { useMemo } from "react";
import { PLOTS, FORCED_MEASURE_GRANULARITY } from "../config/plots";
import { MEASURES } from "../config/metrics";
import type { PreparedData, ContinuousData, DimensionSeries } from "../config/data";
import type { Timeseries } from "../types";
import { computeXmr } from "../metrics/xmr";
import { computeDistributionStats } from "../metrics/distributionStats";
import { resampleTimeseries } from "./resample";
import { useControl } from "../control/ControlContext";
import type { TimeGrain } from "../config/plots";

const MEASURE_MAP = new Map(MEASURES.map(m => [m.id, m]));

function isTimeseries(data: unknown): data is Timeseries {
  return Array.isArray((data as Timeseries)?.timestamps);
}

function isContinuousData(data: unknown): data is ContinuousData {
  return isTimeseries((data as ContinuousData)?.mean);
}

function lastPointIsOutlier(ts: Timeseries, logScale: boolean): boolean {
  if (ts.timestamps.length < 3) return false;

  let lastIdx = ts.data.length - 1;
  while (lastIdx >= 0 && ts.data[lastIdx] == null) lastIdx--;
  if (lastIdx < 0) return false;

  const lastTimestamp = ts.timestamps[lastIdx];
  const xmr = computeXmr(ts, { transform: logScale ? "log" : "identity" });

  return xmr.anomalies.some(anomaly => lastTimestamp >= anomaly.start && lastTimestamp <= anomaly.end);
}

export function useOutlierPlots(preparedData: PreparedData | null): Set<string> {
  const { timeRange, grainOverride } = useControl();

  const globalGrain = useMemo((): TimeGrain => {
    if (grainOverride) return grainOverride;
    if (!timeRange.start || !timeRange.end) return "day";
    const diffDays = (timeRange.end.getTime() - timeRange.start.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays > 63 ? "week" : "day";
  }, [grainOverride, timeRange]);

  return useMemo(() => {
    if (!preparedData) return new Set();

    const outlierIds = new Set<string>();

    for (const plot of PLOTS) {
      const chartType = plot.chart;

      if (chartType === "distribution-xmr" && plot.distributionXmr) {
        // For distribution-xmr, compute the mean series the same way the chart does,
        // including the same weekly resampling applied in Plots.tsx via displayDimensionData.
        let dimensionData = preparedData[plot.measureId]?.[plot.distributionXmr.dimension] as
          DimensionSeries | undefined;
        if (!dimensionData) continue;
        if (globalGrain === "week") {
          const measure = MEASURE_MAP.get(plot.measureId);
          const strategy: "sum" | "average" = measure?.isCumulative ? "sum" : "average";
          const resampled: DimensionSeries = {};
          for (const [key, series] of Object.entries(dimensionData)) {
            resampled[key] = resampleTimeseries(series, "week", strategy);
          }
          dimensionData = resampled;
        }
        const stats = computeDistributionStats(dimensionData, plot.distributionXmr.distribution);
        if (lastPointIsOutlier(stats.meanSeries, false)) {
          outlierIds.add(plot.id);
        }
        continue;
      }

      if (chartType !== "xmr" && chartType !== "xmr-log" && chartType !== "x") continue;

      const allData = preparedData[plot.measureId]?.["all"];
      if (!allData) continue;

      let ts: Timeseries | undefined;
      if (isTimeseries(allData)) {
        ts = allData;
      } else if (isContinuousData(allData)) {
        ts = allData.mean;
      }

      if (!ts) continue;

      // Apply same resampling as Plots.tsx
      if (globalGrain === "week") {
        const isForcedDaily = FORCED_MEASURE_GRANULARITY[plot.measureId] === "day";
        const measure = MEASURE_MAP.get(plot.measureId);
        const strategy: "sum" | "average" = measure?.isCumulative && !isForcedDaily ? "sum" : "average";
        ts = resampleTimeseries(ts, "week", strategy);
      }

      if (lastPointIsOutlier(ts, chartType === "xmr-log")) {
        outlierIds.add(plot.id);
      }
    }

    return outlierIds;
  }, [preparedData, globalGrain]);
}
